//@flow
import React from 'react';
import { themr, type ThemeType } from 'react-css-themr';
import throttle from 'lodash/throttle';

import { CanvasShapeEnum } from 'common/enums/CanvasEnum';
import { ALIGNMENT, ACTIVATE_ON } from './FpLocationMarker';
import theme from './fpVisitMarkerFactory.scss';
import FpMarkerWithHighlightFactory from './FpMarkerWithHighlight';
import CanvasUtil from 'common/utils/CanvasUtil';
import { Color } from 'common/models/internal/Color';

import { type BeatVisit } from 'common/models/internal/BeatVisit';
import type { VisitIdOutletType } from 'common/entity/pjpMaster/planView/FpPlanDetailsContainer';

type Props = {
    visit: BeatVisit,
    beatId: ?string,
    label: string | number,
    color: Array<Color> | Color,
    isHovered?: boolean,
    isPopupOpen?: boolean,
    isContextMenuOpen: boolean,
    isPlanEditable?: boolean,
    getContainer: () => string,
    lat: number,
    lng: number,
    salesCategory: string,
    salesmanId: ?string,
    isSelected: boolean,
    markerType: string,
    onMarkerPopUpCloseClick: () => void,
    outletData: ?VisitIdOutletType,
    status: string,
    selectedVisit?: string,
    onVisitMarkerClick: (beatId: string, visit: BeatVisit) => void,
    sourceIndex: number,
    sourceId: number,
    visitIndex: number,
    $hover?: boolean,
    theme: ThemeType,
    isUnallocated?: boolean,
    $hover: boolean,
    withArrow?: boolean,
    disableContextMenu?: boolean,
    onHover: (number, number) => void,
    onHoverOut: () => void,
    onDisabledElementHover: () => void,
    onContextMenu?: (
        x: number,
        y: number,
        beatId: string,
        visitId: string
    ) => void,
    isDisabled: boolean,
    isMarkerInSpider?: boolean,
    onPopUpCloseClick: Function,
    sourceId: string,
    $onMouseAllow: Function,
    parentElRefForMarker: {
        current: ?HTMLDivElement
    }
};

type DefaultProps = {
    minimal: boolean,
    withArrow: boolean
};

const MARKER_WIDTH = 20;
const MARKER_HEIGHT = 24;

const fpVisitMarkerFactory = (
    {
        VisitMarkerPopup,
        VisitMarkerPopupOuter
    }: {
        VisitMarkerPopup: React$Element<*>,
        VisitMarkerPopupOuter: React$Element<*>
    },
    options: { themed: boolean }
) => {
    const Marker = FpMarkerWithHighlightFactory(
        {
            PopupOuter: VisitMarkerPopupOuter,
            PopupInner: VisitMarkerPopup
        },
        options
    );
    return makeVisitMarker({ Marker });
};

const makeVisitMarker = ({ Marker }) => {
    class FpVisitMarker extends React.PureComponent<Props> {
        hovered: boolean;
        static defaultProps: DefaultProps = {
            canvasShape: CanvasShapeEnum.SquareWithArrow,
            isCanvasMarker: true,
            drawOnCanvas: drawPlanVisitMarkerOnCanvas,
            minimal: false,
            sideLength: 20,
            canvasIconFontSize: 10,
            labelFontWeight: 700,
            labelFontSize: 10,
            withArrow: true,
            isDisabled: false
        };

        constructor(props: Props) {
            super(props);
            this.emitOnHover = throttle(this.emitOnHover, 300, {
                leading: false
            });
            this.hovered = false;
        }

        UNSAFE_componentWillReceiveProps(nextProps: Props) {
            const { $hover } = nextProps;
            if (!this.props.$hover && $hover) {
                this.hovered = true;
                this.emitOnHover();
            } else if (this.props.$hover && !$hover) {
                this.onHoverOut();
            }
        }

        componentWillUnmount() {
            this.onHoverOut();
        }

        emitOnHover = () => {
            const { hovered } = this;
            if (!hovered) {
                return;
            }
            const {
                beatId,
                visit,
                onHover,
                isUnallocated,
                onUnallocatedVisitHover
            } = this.props;
            if (isUnallocated) {
                onUnallocatedVisitHover(visit.id);
            } else {
                onHover(beatId, visit.outletVisitId || visit.id);
            }
        };

        onHoverOut = () => {
            const { hovered } = this;
            if (!hovered) {
                return;
            }
            const {
                onHoverOut,
                isUnallocated,
                onUnallocatedVisitHoverOut
            } = this.props;
            if (isUnallocated) {
                onUnallocatedVisitHoverOut();
            } else {
                onHoverOut();
            }
            this.hovered = false;
        };

        onPopUpCloseClick = (event?: Event) => {
            event?.stopPropagation();
            const {
                onMarkerPopUpCloseClick,
                $onMouseAllow,
                isUnallocated,
                onUnallocatedVisitPopUpCloseClick
            } = this.props;
            $onMouseAllow(true);
            if (isUnallocated) {
                onUnallocatedVisitPopUpCloseClick();
            } else {
                onMarkerPopUpCloseClick();
            }
        };

        onClick = () => {
            const {
                onVisitMarkerClick,
                beatId,
                visit,
                isUnallocated,
                onUnallocatedVisitClick
            } = this.props;
            if (isUnallocated) {
                onUnallocatedVisitClick(visit.id);
            } else {
                onVisitMarkerClick(beatId, visit.outletVisitId || visit.id);
            }
        };

        onContextMenu = e => {
            const {
                onContextMenuOpen,
                beatId,
                visit,
                isUnallocated,
                onContextMenuUnallocatedVisits
            } = this.props;
            if (isUnallocated) {
                onContextMenuUnallocatedVisits(e.clientX, e.clientY, visit.id);
            } else {
                onContextMenuOpen(
                    e.clientX,
                    e.clientY,
                    beatId,
                    visit.outletVisitId
                );
            }
        };

        render() {
            const {
                theme,
                visit,
                label,
                isUnallocated,
                isHovered,
                isPopupOpen,
                $hover,
                withArrow,
                color,
                icon,
                ...others
            } = this.props;
            const colorCode = Array.isArray(color)
                ? color.map(c => c.code)
                : color.code;
            const colorName = Array.isArray(color)
                ? color.map(c => c.name)
                : color.name;
            const { id, outletVisitId } = visit;
            return (
                <Marker
                    {...others}
                    height={MARKER_HEIGHT}
                    width={MARKER_WIDTH}
                    color={colorName}
                    colorCode={colorCode}
                    dangerouslyDisablePopupHover
                    isUnallocated={isUnallocated}
                    visit={visit}
                    label={label}
                    key={`${outletVisitId || label}/${id}`}
                    showPopupOn={ACTIVATE_ON.SELFHOVERSELECT}
                    $hover={$hover}
                    isHovered={isHovered}
                    isPopupOpen={isPopupOpen}
                    markerAlignment={ALIGNMENT.Default}
                    withArrow={withArrow}
                    markerIcon={isUnallocated ? 'information' : icon || label}
                    theme={theme}
                    onPopUpCloseClick={this.onPopUpCloseClick}
                    onContextMenu={this.onContextMenu}
                    onClick={this.onClick}
                />
            );
        }
    }

    const ThemedVisitMarker = themr('FpVisitMarker', theme)(FpVisitMarker);
    return { ThemedVisitMarker, FpVisitMarker };
};

const drawPlanVisitMarkerOnCanvas = ({ ctx, x, y, props }) => {
    const {
        color,
        label,
        canvasShape,
        labelFontSize,
        sideLength,
        minimal,
        stripeColor,
        isUnallocated,
        iconColor
    } = props;
    const arrowLength = 5;
    let { canvasIconFontSize } = props;
    if (isUnallocated) {
        // #HACK overriding unalloacted icon size
        canvasIconFontSize = 10;
    }
    if (label === 'homebase') {
        // #HACK overriding homebase icon size
        canvasIconFontSize = 14;
    }
    const colorCode = Array.isArray(color)
        ? color.map(c => c.code)
        : color.code;
    CanvasUtil.drawShape({
        shape: canvasShape,
        ctx,
        x,
        y,
        color: colorCode,
        opacity: 1,
        sideLength,
        arrowLength,
        label,
        minimal,
        canvasIconFontSize,
        stripeColor: stripeColor?.code,
        withTextShadow: !!stripeColor,
        labelFontSize,
        iconColor
    });
};

export default fpVisitMarkerFactory;
